import axios from "axios";
import CryptoJS from "crypto-js";

const SECRET_KEY = "1234567890abcdef1234567890abcdef";

// 🔐 Encrypt
const encryptPayload = (data) => {
  return CryptoJS.AES.encrypt(
    JSON.stringify(data),
    SECRET_KEY
  ).toString();
};

/* ================= DECRYPT ================= */
const decryptFunction = (ciphertext) => {

  try {
    const bytes = CryptoJS.AES.decrypt(ciphertext, SECRET_KEY);
    return JSON.parse(bytes.toString(CryptoJS.enc.Utf8));
  } catch (e) {
    console.error("Decryption failed:", e);
    return ciphertext;
  }
};

const CallReview_API = process.env.REACT_APP_CALL_REVIEW ;

/* Every other service in this app goes through `getRequest`/`postRequest`,
 * which attach the bearer token. The three reads below call axios directly and
 * so have to attach it themselves — without this they reach crs-details with no
 * credentials at all, and the Call Review sections render the backend's "No
 * token provided" in place of the scorecard. */
const authHeaders = () => {
  const authToken = localStorage.getItem("authToken");
  return authToken ? { Authorization: `Bearer ${authToken}` } : {};
};

// 📡 GET Call Review
export const getCallReview = async (callId) => {
  const BASE_URL = CallReview_API;

  try {
    if (!callId) {
      throw new Error("CallId is missing");
    }

  

    const url = `${BASE_URL}&callid=${callId}`;

    // console.log("🚀 API URL:", url);

    const response = await axios.get(url, { headers: authHeaders() });

    if (!response || !response.data) {
      throw new Error("Empty response from server");
    }

    // console.log("📦 Raw Response:", response.data);

    // 🔓 decrypt safely
    try {
      return decryptFunction(response.data);
      
    } catch (e) {
      console.warn("⚠️ Response not encrypted, returning raw");
      return response.data;
    }

  } catch (error) {
    console.error("❌ API Error:", error);

    // Axios specific error handling
    if (error.response) {
      const err = new Error(error.response.data?.message || "Server Error");
      err.status = error.response.status;
      throw err;
    } else if (error.request) {
      throw new Error("No response from server");
    } else {
      throw new Error(error.message || "Something went wrong");
    }
  }
};

// 📡 Executive/call summary for a call — used by the feedback-only PDF for
// call categories that have no CRS scoring. Returns { executiveSummary }.
export const getCallExecutiveSummary = async (callId) => {
  const BASE_URL = CallReview_API;
  if (!callId) return { executiveSummary: "" };
  try {
    const url = `${BASE_URL}&type=summary&callid=${callId}`;
    const response = await axios.get(url, { headers: authHeaders() });
    const data = decryptFunction(response.data);
    return data && typeof data === "object" ? data : { executiveSummary: "" };
  } catch (error) {
    console.error("getCallExecutiveSummary error:", error);
    return { executiveSummary: "" };
  }
};

// 📡 Saved reviewer feedback for a non-scoring call, so the feedback modal can
// reopen in edit mode. Returns { whatWentWell, whatCanImprove, generalComments }.
const EMPTY_FEEDBACK = {
  whatWentWell: "",
  whatCanImprove: "",
  generalComments: "",
};
export const getCallReviewFeedback = async (callId) => {
  const BASE_URL = CallReview_API;
  if (!callId) return { ...EMPTY_FEEDBACK };
  try {
    const url = `${BASE_URL}&type=feedback&callid=${callId}`;
    const response = await axios.get(url, { headers: authHeaders() });
    const data = decryptFunction(response.data);
    return data && typeof data === "object"
      ? {
          ...EMPTY_FEEDBACK,
          ...data,
          // Keep the persistence timestamp available to the PDF flow. The
          // backend should return this from the feedback record's updatedAt
          // column when the reviewer saves or edits feedback.
          updatedAt: data.updatedAt || data.UpdatedAt || data.reviewedAt,
        }
      : { ...EMPTY_FEEDBACK };
  } catch (error) {
    console.error("getCallReviewFeedback error:", error);
    return { ...EMPTY_FEEDBACK };
  }
};
